import { useEffect, useRef, useState } from 'react'
import { AdvancedApiError, advancedGet, isAdvancedUnauthorized } from './client'
import { AdvancedPatientOverview } from './types'
import { useAdvancedResource } from './useAdvancedResource'

export type OverviewState =
  | { status: 'loading' }
  | { status: 'ready'; overview: AdvancedPatientOverview; retry: () => void }
  // 404 is not-on-care-team; do not surface that as a distinct empty state (ADR-016).
  | { status: 'unavailable' }
  | { status: 'error'; unauthorized: boolean; retry: () => void }

export const OVERVIEW_POLL_MS = 60_000

// No uid (the standalone caseload with no patient open) fetches nothing and stays loading.
export const useAdvancedOverview = (uid: string | undefined): OverviewState => {
  const resource = useAdvancedResource<AdvancedPatientOverview>(
    (signal) => advancedGet<AdvancedPatientOverview>(`/patients/${encodeURIComponent(uid ?? '')}`, signal),
    [uid],
    uid === undefined
  )
  // The resource keeps the last patient's state until its effect resets it, so only a state set since the uid changed is this uid's.
  const [uidChange, setUidChange] = useState({ uid, resource })
  if (uidChange.uid !== uid) {
    setUidChange({ uid, resource })
  }
  const current = uidChange.uid === uid && resource !== uidChange.resource
  const ready = useRef(false)
  ready.current = current && resource.status === 'ready'
  const { retry } = resource

  // Picks up another provider's track change; revalidates in place, so no loader (ADR-092).
  useEffect(() => {
    const revalidate = () => {
      if (ready.current && document.visibilityState === 'visible') {
        retry()
      }
    }
    const timer = window.setInterval(revalidate, OVERVIEW_POLL_MS)
    window.addEventListener('focus', revalidate)
    document.addEventListener('visibilitychange', revalidate)

    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', revalidate)
      document.removeEventListener('visibilitychange', revalidate)
    }
  }, [retry])

  if (uid === undefined || !current) {
    return { status: 'loading' }
  }

  if (resource.status === 'ready') {
    return { status: 'ready', overview: resource.data, retry: resource.retry }
  }

  if (resource.status === 'error') {
    const notFound = resource.error instanceof AdvancedApiError && resource.error.status === 404

    return notFound
      ? { status: 'unavailable' }
      : { status: 'error', unauthorized: isAdvancedUnauthorized(resource.error), retry: resource.retry }
  }

  return { status: 'loading' }
}
